import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLButtonElement} [previousButton] - Previous media button.
 * @property {HTMLButtonElement} [nextButton] - Next media button.
 * @property {HTMLButtonElement} [zoomOutButton] - Zoom out button.
 * @property {HTMLButtonElement} [zoomInButton] - Zoom in button.
 * @property {HTMLElement} [zoomValue] - Current zoom percentage.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.addEventListener('drag-zoom-change', this.#handleZoomChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.removeEventListener('drag-zoom-change', this.#handleZoomChange);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    this.#setActiveMedia(index, { resetZoom: true });

    const open = () => {
      dialog.showModal();

      if (this.#isDesktop()) {
        targetThumbnail?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
      } else {
        for (const target of [targetThumbnail, targetImage]) {
          target?.scrollIntoView({ behavior: 'instant' });
        }
      }

      this.#updateNavigationControls();
      this.#updateZoomControls();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (this.#isDesktop()) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#setActiveMedia(activeIndex, { resetZoom: activeIndex !== this.#activeIndex });
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPrevious();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNext();
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = this.#isDesktop() || prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  showPrevious() {
    if (this.#activeIndex <= 0) return;
    this.selectThumbnail(this.#activeIndex - 1, { behavior: 'instant' });
  }

  showNext() {
    if (this.#activeIndex >= this.refs.media.length - 1) return;
    this.selectThumbnail(this.#activeIndex + 1, { behavior: 'instant' });
  }

  zoomIn() {
    const wrapper = this.#activeZoomWrapper;
    if (!wrapper) return;
    this.#updateZoomControls(wrapper.zoomIn());
  }

  zoomOut() {
    const wrapper = this.#activeZoomWrapper;
    if (!wrapper) return;
    this.#updateZoomControls(wrapper.zoomOut());
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.media.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];

    if (targetImage) {
      this.#setActiveMedia(index, { resetZoom: index !== this.#activeIndex });

      if (!this.#isDesktop()) {
        targetImage.scrollIntoView({
          behavior: options.behavior,
        });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #setActiveMedia(index, options = { resetZoom: true }) {
    const { media } = this.refs;
    const previousIndex = this.#activeIndex;

    if (options.resetZoom && previousIndex !== index) {
      this.#resetMediaZoom(media[previousIndex]);
      this.#resetMediaZoom(media[index]);
    }

    media.forEach((item, itemIndex) => {
      item.toggleAttribute('data-active', itemIndex === index);
      item.setAttribute('aria-hidden', `${itemIndex !== index}`);
    });

    this.#activeIndex = index;
    this.#updateNavigationControls();
    this.#updateZoomControls();
  }

  #resetMediaZoom(mediaItem) {
    const wrapper = mediaItem?.querySelector('drag-zoom-wrapper');
    wrapper?.resetZoom?.();
  }

  get #activeZoomWrapper() {
    return /** @type {DragZoomWrapperElement | null} */ (
      this.refs.media[this.#activeIndex]?.querySelector('drag-zoom-wrapper') ?? null
    );
  }

  #updateNavigationControls() {
    const { previousButton, nextButton, media } = this.refs;

    if (previousButton instanceof HTMLButtonElement) {
      previousButton.disabled = this.#activeIndex <= 0;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = this.#activeIndex >= media.length - 1;
    }
  }

  #updateZoomControls(scale = this.#activeZoomWrapper?.scale ?? 1) {
    const { zoomOutButton, zoomInButton, zoomValue } = this.refs;
    const hasImage = Boolean(this.#activeZoomWrapper);

    if (zoomOutButton instanceof HTMLButtonElement) {
      zoomOutButton.disabled = !hasImage || scale <= 1;
    }

    if (zoomInButton instanceof HTMLButtonElement) {
      zoomInButton.disabled = !hasImage || scale >= 5;
    }

    if (zoomValue instanceof HTMLElement) {
      zoomValue.textContent = `${Math.round(scale * 100)}%`;
    }
  }

  #handleZoomChange = (event) => {
    if (event.target !== this.#activeZoomWrapper) return;
    const scale = Number(/** @type {CustomEvent} */ (event).detail?.scale ?? 1);
    this.#updateZoomControls(scale);
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}

/**
 * @typedef {HTMLElement} DragZoomWrapperElement
 * @property {number} scale
 * @property {() => number} zoomIn
 * @property {() => number} zoomOut
 * @property {() => void} resetZoom
 */
